import React from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Container, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import { FileDownload, Refresh } from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import Navbar from '../setup/Navbar';
import Loading from '../../BuiltInComponent/Loading';
import '../../services/GlobalVariables';

const localDate = (date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

export default function LoginHistoryReport() {
  const { user } = useSelector((state) => state.auth);
  const today = React.useMemo(() => new Date(), []);
  const [fromDate, setFromDate] = React.useState(localDate(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate] = React.useState(localDate(today));
  const [selectedUserID, setSelectedUserID] = React.useState('all');
  const [deviceType, setDeviceType] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [users, setUsers] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [summary, setSummary] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!user?.userID) return;
    axios.post(`${global.SET_API_URL}users/getAllUsers`, {
      cmpID: user.cmpID,
      branchID: user.branchID || user.defaultBranchID || 1,
      includeAll: true,
    }).then((response) => setUsers(Array.isArray(response.data) ? response.data : []))
      .catch(() => setUsers([]));
  }, [user]);

  const loadReport = React.useCallback(async () => {
    if (!user?.userID) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${global.SET_API_URL}users/loginHistoryReport`, {
        params: { loggedInUserID: user.userID, fromDate, toDate, selectedUserID, deviceType, search: search || undefined },
      });
      setRows(response.data?.rows || []);
      setSummary(response.data?.summary || {});
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load login history');
    } finally {
      setLoading(false);
    }
  }, [user?.userID, fromDate, toDate, selectedUserID, deviceType, search]);

  React.useEffect(() => { loadReport(); }, [loadReport]);

  const exportExcel = () => {
    const data = rows.map((row) => ({
      'Login Time': row.loginAt ? new Date(row.loginAt).toLocaleString() : '',
      User: row.dispName || row.userName || `User #${row.userID}`,
      Username: row.userName || '', 'User Type': row.userType || '',
      Branch: row.branchName || row.branchID || '', 'IP Address': row.ipAddress || '',
      'Forwarded IP': row.forwardedFor || '', System: row.systemName || '',
      Device: row.deviceType || '', OS: row.operatingSystem || '', Browser: row.browser || '',
      Resolution: row.screenResolution || '', Timezone: row.timezone || '', Language: row.language || '',
      'User Agent': row.userAgent || '',
    }));
    const sheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Login History');
    XLSX.writeFile(workbook, `Login_History_${fromDate || 'all'}_${toDate || 'all'}.xlsx`);
  };

  const selectedUser = users.find((item) => Number(item.userID) === Number(selectedUserID)) || null;
  const deviceBreakdown = React.useMemo(() => {
    const counts = {};
    rows.forEach((row) => { const key = row.deviceType || 'Unknown'; counts[key] = (counts[key] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  return <>
    <Navbar />
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight={800}>Login History Report</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Successful user logins with IP, device, operating system and browser details.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
          <TextField size="small" fullWidth type="date" label="From Date" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" fullWidth type="date" label="To Date" value={toDate}
            onChange={(e) => setToDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <Autocomplete size="small" fullWidth options={users} value={selectedUser}
            getOptionLabel={(option) => option?.dispName || option?.userName || `User #${option?.userID || ''}`}
            isOptionEqualToValue={(option, value) => Number(option.userID) === Number(value.userID)}
            onChange={(event, value) => setSelectedUserID(value?.userID || 'all')}
            renderInput={(params) => <TextField {...params} label="User (All)" />} />
          <TextField select size="small" fullWidth label="Device" value={deviceType} onChange={(e) => setDeviceType(e.target.value)}>
            <MenuItem value="all">All Devices</MenuItem><MenuItem value="desktop">Desktop</MenuItem>
            <MenuItem value="mobile">Mobile</MenuItem><MenuItem value="tablet">Tablet</MenuItem>
          </TextField>
          <TextField size="small" fullWidth label="Search" placeholder="User, IP, system, OS, browser"
            value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button variant="contained" startIcon={<Refresh />} onClick={loadReport} disabled={loading} sx={{ minWidth: 130 }}>Refresh</Button>
          <Button variant="outlined" color="success" startIcon={<FileDownload />} onClick={exportExcel}
            disabled={!rows.length} sx={{ minWidth: 150 }}>Excel</Button>
        </Stack>
      </Paper>

      {loading && <Box sx={{ mb: 2 }}><Loading loadingText="Loading login history" /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2 }}>
        {[
          ['Total Logins', summary.totalLogins || 0], ['Unique Users', summary.uniqueUsers || 0],
          ['Unique IPs', summary.uniqueIPs || 0], ['Unique Systems', summary.uniqueSystems || 0],
          ['Latest Login', summary.latestLoginAt ? new Date(summary.latestLoginAt).toLocaleString() : '-'],
        ].map(([label, value]) => <Paper key={label} variant="outlined" sx={{ p: 2, borderTop: '4px solid', borderTopColor: 'primary.main' }}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h6" fontWeight={800}>{value}</Typography>
        </Paper>)}
      </Box>

      {deviceBreakdown.length > 0 && <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        {deviceBreakdown.map(([device, count]) => <Chip key={device} label={`${device}: ${count}`} variant="outlined" color="primary" />)}
      </Stack>}

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead><TableRow>
            <TableCell>Login Time</TableCell><TableCell>User</TableCell><TableCell>IP Address</TableCell>
            <TableCell>System / Device</TableCell><TableCell>OS / Browser</TableCell>
            <TableCell>Screen / Language</TableCell><TableCell>Branch</TableCell><TableCell>Timezone</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {rows.map((row) => <TableRow key={row.loginHistoryID} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.loginAt ? new Date(row.loginAt).toLocaleString() : '-'}</TableCell>
              <TableCell><Typography variant="body2" fontWeight={700}>{row.dispName || row.userName || `User #${row.userID}`}</Typography>
                <Typography variant="caption" color="text.secondary">{row.userName || '-'} · {row.userType || '-'}</Typography></TableCell>
              <TableCell><Typography variant="body2" fontFamily="monospace">{row.ipAddress || '-'}</Typography>
                {row.forwardedFor && row.forwardedFor !== row.ipAddress && <Tooltip title={row.forwardedFor}><Typography variant="caption" color="text.secondary" noWrap>Proxy: {row.forwardedFor}</Typography></Tooltip>}</TableCell>
              <TableCell><Typography variant="body2">{row.systemName || '-'}</Typography><Chip size="small" label={row.deviceType || 'Unknown'} /></TableCell>
              <TableCell><Typography variant="body2">{row.operatingSystem || '-'}</Typography><Typography variant="caption" color="text.secondary">{row.browser || '-'}</Typography></TableCell>
              <TableCell><Typography variant="body2">{row.screenResolution || '-'}</Typography><Typography variant="caption" color="text.secondary">{row.language || '-'}</Typography></TableCell>
              <TableCell>{row.branchName || row.branchID || '-'}</TableCell><TableCell>{row.timezone || '-'}</TableCell>
            </TableRow>)}
            {!rows.length && !loading && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}>No login records found for the selected filters.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Showing {rows.length} successful login record(s). Data is restricted to your company and branch.
      </Typography>
    </Container>
  </>;
}